import { supabase } from './supabase';
import { markOpportunitiesReadOnActivity } from './crm/markOpportunityRead';
import { resolveCurrentSender } from './messagesCore';

export type ActivityType =
  | 'sms_sent'
  | 'email_sent'
  | 'proposal_sent'
  | 'phone_call'
  | 'stage_moved'
  | 'status_changed'
  | 'follow_up_scheduled'
  | 'follow_up_cleared'
  | 'follow_up_completed'
  | 'proposal_linked'
  | 'booking_linked'
  | 'project_linked'
  | 'field_quote_linked'
  | 'pipeline_changed';

interface RecordActivityParams {
  businessId: string;
  customerId: string;
  activityType: ActivityType;
  activityData: Record<string, any>;
  specificOpportunityId?: number; // Optional: if provided, only record for this opportunity
  createdBy?: string; // Resource/user ID who performed the action
}

/**
 * Records an activity for open CRM opportunities
 *
 * If specificOpportunityId is provided, records activity only for that opportunity.
 * Otherwise, finds all open opportunities for the customer and records activity for each.
 * Any recorded activity marks the affected opportunity/opportunities as read.
 */
export async function recordOpportunityActivity({
  businessId,
  customerId,
  activityType,
  activityData,
  specificOpportunityId,
  createdBy
}: RecordActivityParams): Promise<boolean> {
  try {
    if (!supabase) {
      console.error('[Opportunity Activities] Supabase client not initialized');
      return false;
    }

    // Sent texts and emails carry who sent them, the same stamp the message
    // row gets, so CRM history reads like the Messages thread.
    if ((activityType === 'sms_sent' || activityType === 'email_sent') && !activityData?.sender) {
      const sender = await resolveCurrentSender(businessId);
      activityData = { ...activityData, sender };
      if (!createdBy && sender.kind === 'resource' && sender.id) createdBy = sender.id;
    }

    let opportunityIds: number[] = [];

    if (specificOpportunityId) {
      opportunityIds = [specificOpportunityId];
      const { error } = await supabase
        .from('opportunity_activities')
        .insert({
          business_id: businessId,
          pipeline_opportunities_id: specificOpportunityId,
          customer_id: customerId,
          activity_type: activityType,
          activity_data: activityData,
          created_by: createdBy || null
        });

      if (error) {
        console.error('[Opportunity Activities] Error recording activity for specific opportunity:', error);
        return false;
      }
      console.log(`[Opportunity Activities] ✅ Recorded ${activityType} for opportunity ${specificOpportunityId}`);
    } else {
      const { data: opportunities, error: opportunitiesError } = await supabase
        .from('pipeline_opportunities')
        .select('id')
        .eq('business_id', businessId)
        .eq('customer_id', customerId)
        .eq('status', 'Open');

      if (opportunitiesError) {
        console.error('[Opportunity Activities] Error fetching opportunities:', opportunitiesError);
        return false;
      }

      if (!opportunities || opportunities.length === 0) {
        console.log(`[Opportunity Activities] No open opportunities found for customer ${customerId}`);
        return false;
      }

      opportunityIds = opportunities.map((opp) => opp.id);
      const activityRecords = opportunities.map(opp => ({
        business_id: businessId,
        pipeline_opportunities_id: opp.id,
        customer_id: customerId,
        activity_type: activityType,
        activity_data: activityData,
        created_by: createdBy || null
      }));

      const { error: insertError } = await supabase
        .from('opportunity_activities')
        .insert(activityRecords);

      if (insertError) {
        console.error('[Opportunity Activities] Error inserting activity records:', insertError);
        return false;
      }
      console.log(`[Opportunity Activities] ✅ Recorded ${activityType} for ${opportunities.length} opportunity/opportunities`);
    }

    try {
      await markOpportunitiesReadOnActivity(supabase, opportunityIds, businessId);
    } catch (readError) {
      console.error('[Opportunity Activities] Error marking opportunities read after activity:', readError);
    }

    return true;
  } catch (error) {
    console.error('[Opportunity Activities] Unexpected error:', error);
    return false;
  }
}
